import { useState, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Building2, Plus, Sparkles, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProveedores, useCreateProveedor } from "@/hooks/useProveedores";
import { useSimilarPartners } from "@/hooks/useSimilarPartners";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { normalizePartnerName, DUPLICATE_NAME_ERROR } from "@/lib/normalizeName";

interface Props {
  value: string;
  onValueChange: (value: string, proveedor?: { id: string; nombre: string }) => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
  allowCreate?: boolean;
  invalid?: boolean;
  defaultSearch?: string;
}

/**
 * Strict combobox for selecting a Proveedor from the org master directory.
 * Returns the proveedor_id (UUID). Mirror of ClienteSelectField.
 *
 * Anti-duplicate shield:
 *  - cmdk filter normalized (trim + collapse spaces + lowercase + NBSP-safe).
 *  - <CommandEmpty> calls find_similar_proveedores RPC and offers a "¿Quisiste decir...?" link.
 *  - Create button surfaces DUPLICATE_NAME errors from the hook with a clear toast.
 */
export function ProveedorSelectField({
  value,
  onValueChange,
  className,
  placeholder = "Seleccionar proveedor…",
  required = false,
  allowCreate = true,
  invalid = false,
  defaultSearch = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState(defaultSearch);
  const { data: proveedores = [], isLoading } = useProveedores();
  const createProveedor = useCreateProveedor();
  const { toast } = useToast();

  const [newNombre, setNewNombre] = useState("");
  const [newNit, setNewNit] = useState("");

  const selected = proveedores.find((p: any) => p.id === value);

  useEffect(() => {
    if (defaultSearch) setSearch(defaultSearch);
  }, [defaultSearch]);

  // ── Capa 3: filtro UI flexibilizado ────────────────────────────────
  // En vez de depender del fuzzy default de cmdk, filtramos manualmente
  // contra el nombre normalizado + NIT. Esto elimina el "callejón visual"
  // donde NBSP, dobles espacios o variaciones de mayúsculas escondían el match.
  const filteredProveedores = useMemo(() => {
    const q = normalizePartnerName(search);
    if (!q) return proveedores;
    return proveedores.filter((p: any) => {
      const haystack = `${normalizePartnerName(p.nombre)} ${(p.nit ?? "").toLowerCase()}`;
      return haystack.includes(q);
    });
  }, [proveedores, search]);

  // ── Capa 4: smart suggestions vía RPC find_similar_proveedores ────
  const showEmpty = filteredProveedores.length === 0 && !isLoading;
  const { data: similar, loading: loadingSimilar } = useSimilarPartners(
    "proveedor",
    search,
    showEmpty,
  );
  const strongSuggestions = similar.filter((s) => s.similarity_score >= 0.6);

  const openCreateWithPrefill = () => {
    setNewNombre(search.trim() || defaultSearch.trim() || "");
    setNewNit("");
    setOpen(false);
    setCreateOpen(true);
  };

  const linkExisting = (id: string, nombre: string) => {
    onValueChange(id, { id, nombre });
    setOpen(false);
    setSearch("");
  };

  const handleCreate = async () => {
    if (!newNombre.trim()) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    try {
      const created = await createProveedor.mutateAsync({
        nombre: newNombre.trim(),
        nit: newNit.trim() || undefined,
      });
      toast({ title: "✅ Proveedor creado" });
      setNewNombre("");
      setNewNit("");
      setCreateOpen(false);
      if (created?.id) {
        onValueChange(created.id, { id: created.id, nombre: created.nombre });
      }
    } catch (err: any) {
      if (err?.message === DUPLICATE_NAME_ERROR || err?.code === "DUPLICATE_NAME") {
        const existing = err?.existing;
        toast({
          title: "Ya existe un proveedor con este nombre",
          description: existing
            ? `"${existing.nombre}" ya está en el directorio. Vinculándolo automáticamente…`
            : "Revisa el directorio antes de crear un duplicado.",
          variant: "destructive",
        });
        if (existing?.id) {
          onValueChange(existing.id, { id: existing.id, nombre: existing.nombre });
          setCreateOpen(false);
        }
        return;
      }
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full max-w-full min-w-0 justify-between font-normal overflow-hidden",
              invalid && "border-destructive ring-2 ring-destructive/30",
              className,
            )}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="block truncate min-w-0 flex-1 text-left">
                {selected ? selected.nombre : (
                  <span className="text-muted-foreground block truncate">
                    {defaultSearch && !value ? `“${defaultSearch}” — sin vincular` : `${placeholder}${required ? " *" : ""}`}
                  </span>
                )}
              </span>
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por nombre o NIT…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {showEmpty && (
                <CommandEmpty>
                  <div className="py-4 px-3 text-left space-y-3">
                    {loadingSimilar ? (
                      <p className="text-sm text-muted-foreground text-center">Buscando coincidencias similares…</p>
                    ) : strongSuggestions.length > 0 ? (
                      <>
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <Sparkles className="h-4 w-4 shrink-0" />
                          <p className="text-sm font-medium">¿Quisiste decir…?</p>
                        </div>
                        <div className="space-y-1.5">
                          {strongSuggestions.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => linkExisting(s.id, s.nombre)}
                              className="w-full flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-left text-sm hover:bg-amber-500/10 transition"
                            >
                              <span className="truncate">{s.nombre}</span>
                              <span className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 shrink-0">
                                <Link2 className="h-3 w-3" />
                                Vincular
                              </span>
                            </button>
                          ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground pt-1">
                          Si ninguno coincide, puedes crear uno nuevo:
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center">
                        No se encontraron proveedores que coincidan.
                      </p>
                    )}
                    {allowCreate && (
                      <Button
                        type="button"
                        variant={strongSuggestions.length > 0 ? "ghost" : "outline"}
                        size="sm"
                        onClick={openCreateWithPrefill}
                        className="w-full gap-2"
                      >
                        <Plus className="h-4 w-4 text-primary" />
                        <span className="truncate">
                          Crear {search.trim() ? `"${search.trim()}"` : "nuevo proveedor"}
                        </span>
                      </Button>
                    )}
                  </div>
                </CommandEmpty>
              )}
              <CommandGroup>
                {filteredProveedores.map((p: any) => (
                  <CommandItem
                    key={p.id}
                    value={p.id}
                    onSelect={() => {
                      onValueChange(p.id, { id: p.id, nombre: p.nombre });
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-4 w-4", value === p.id ? "opacity-100" : "opacity-0")} />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{p.nombre}</span>
                      {p.nit && (
                        <span className="text-[10px] text-muted-foreground">NIT: {p.nit}</span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
              {allowCreate && filteredProveedores.length > 0 && (
                <CommandGroup>
                  <CommandItem
                    value="__create__"
                    onSelect={openCreateWithPrefill}
                    className="text-primary"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {search.trim()
                      ? `Crear "${search.trim()}"…`
                      : "Crear nuevo proveedor…"}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Nuevo Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="prov_nombre">Razón Social *</Label>
              <Input
                id="prov_nombre"
                value={newNombre}
                onChange={(e) => setNewNombre(e.target.value)}
                placeholder="Shenzhen Electronics Co. Ltd."
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prov_nit">NIT / Tax ID</Label>
              <Input
                id="prov_nit"
                value={newNit}
                onChange={(e) => setNewNit(e.target.value)}
                placeholder="900.123.456-7"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={createProveedor.isPending}>
              {createProveedor.isPending ? "Creando…" : "Crear proveedor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
